import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  auth,
  db 
} from '../config/firebase';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const userRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userRef);
          if (!userDoc.exists()) {
            await setDoc(userRef, {
              email: firebaseUser.email || null,
              isAdmin: false,
              ownedNumbers: [],
              createdAt: new Date().toISOString(),
            });
          }
          const profile = (await getDoc(userRef)).data();
          setUser({
            ...firebaseUser,
            isAdmin: profile?.isAdmin || false,
          });
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Auth profile load failed:', err);
        setUser(firebaseUser ? { ...firebaseUser, isAdmin: false } : null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signup = async (email, password) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, 'users', user.uid), {
      email,
      isAdmin: false,
      createdAt: new Date().toISOString()
    });
    return user;
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const { user } = await signInWithPopup(auth, provider);
    
    // Check if user document exists, if not create it
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        isAdmin: false,
        createdAt: new Date().toISOString()
      });
    }
    return user;
  };

  const value = {
    user,
    loading,
    signup,
    login: (email, password) => signInWithEmailAndPassword(auth, email, password),
    loginWithGoogle,
    logout: () => signOut(auth)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext); 